/**
 * Level 18: Volcanic Depths
 * 
 * Deep underground volcanic caverns with exploding enemies.
 * Intense level featuring lava rivers, unstable platforms, and
 * enemies that explode when the player gets too close.
 * 
 * Design philosophy: Risk/reward gameplay - exploding enemies guard
 * valuable star positions, requiring careful approach or quick escapes.
 * 
 * @module configs/levels/Level18_VolcanicDepths
 */

/** @type {import('../LevelTypes').LevelConfig} */
const Level18_VolcanicDepths = {
    id: 18,
    name: 'Volcanic Depths',
    subtitle: 'Infernal Descent',
    description: 'Survive explosive enemies in the heart of an active volcano.',

    worldWidth: 5000,
    worldHeight: 2200,
    playerStart: { x: 120, y: 500 },
    gravity: 1.0,

    theme: {
        bgGradient: ['#1a0500', '#330a00'],
        platformColor: 0x553322,
        accentColor: 0xff4400,
        particleColor: 0xff6600,
        ambient: 'volcano'
    },

    platforms: [
        // Caldera rim (starting area - descend into volcano)
        { x: 0, y: 600, w: 350, h: 50 },
        { x: 400, y: 750, w: 180, h: 25 },
        { x: 650, y: 900, w: 200, h: 30 },

        // Magma chamber ledges
        { x: 900, y: 1050, w: 250, h: 35 },
        { x: 1200, y: 1200, w: 180, h: 25 },
        { x: 1000, y: 1350, w: 200, h: 30 },

        // Obsidian bridge
        { x: 1300, y: 1500, w: 400, h: 45 },
        { x: 1750, y: 1350, w: 200, h: 30 },

        // Lava tube passage
        { x: 2000, y: 1500, w: 300, h: 40 },
        { x: 2350, y: 1350, w: 180, h: 25 },
        { x: 2600, y: 1200, w: 200, h: 30 },

        // Eruption chamber
        { x: 2850, y: 1400, w: 350, h: 50 },
        { x: 3250, y: 1250, w: 200, h: 30 },
        { x: 3500, y: 1100, w: 250, h: 35 },

        // Escape ascent
        { x: 3800, y: 1300, w: 200, h: 30 },
        { x: 3700, y: 1100, w: 150, h: 25 },
        { x: 3900, y: 900, w: 200, h: 30 },
        { x: 3750, y: 700, w: 180, h: 25 },

        // Exit platform
        { x: 4050, y: 850, w: 300, h: 45 },
        { x: 4400, y: 700, w: 250, h: 40 },
        { x: 4700, y: 900, w: 300, h: 50 }
    ],

    movingPlatforms: [
        // Floating rock over lava
        { x: 1550, y: 1580, w: 120, h: 22, moveX: 180, speed: 0.003 },
        // Magma elevator
        { x: 2500, y: 1280, w: 130, h: 22, moveY: 150, speed: 0.002 },
        // Escape platforms
        { x: 3850, y: 1200, w: 110, h: 20, moveY: 160, speed: 0.0025 },
        { x: 4250, y: 770, w: 120, h: 20, moveX: 120, speed: 0.003 }
    ],

    spikes: [
        // Obsidian spikes
        { x: 1350, y: 1500 },
        { x: 1400, y: 1500 },
        { x: 1450, y: 1500 },
        { x: 2050, y: 1500 },
        { x: 2100, y: 1500 },
        { x: 2900, y: 1400 },
        { x: 2950, y: 1400 },
        { x: 4100, y: 850 },
        { x: 4150, y: 850 }
    ],

    stars: [
        { x: 650, y: 800 },
        { x: 1100, y: 1250 },
        { x: 1750, y: 1250 },
        { x: 2600, y: 1100 },
        { x: 3500, y: 1000 },
        { x: 4400, y: 600 }
    ],

    enemies: [
        // Regular patrol
        { x: 1400, y: 1450 },
        // Exploding enemies (new!) - placed near valuable stars
        { x: 950, y: 1000, type: 'exploding', options: { detectionRange: 120, fuseDuration: 1600, speed: 0.8, walkDist: 70 } },
        { x: 2050, y: 1450, type: 'exploding', options: { detectionRange: 130, fuseDuration: 1400, speed: 0.9, walkDist: 80 } },
        { x: 2900, y: 1350, type: 'exploding', options: { detectionRange: 150, fuseDuration: 1200, speed: 1.0, walkDist: 90 } },
        { x: 3550, y: 1050, type: 'exploding', options: { detectionRange: 130, fuseDuration: 1500, speed: 0.8, walkDist: 60 } },
        // Flying enemy over lava
        { x: 2200, y: 1200, type: 'flying', options: { amplitude: 50, speed: 1.5, walkDist: 150 } },
        // Shield guard at exit
        { x: 4500, y: 650, type: 'shield', options: { speed: 1.0, walkDist: 80, slamInterval: 2500 } }
    ],

    door: { x: 4900, y: 750, w: 50, h: 300 },
    button: { x: 4750, y: 895 },
    box: { x: 4100, y: 750, floating: false },
    goal: { x: 4950, y: 800 },

    lavaZones: [
        // Main lava river
        { x: 1200, y: 1600, w: 900, h: 600 },
        // Eruption chamber floor
        { x: 2800, y: 1550, w: 500, h: 650 },
        // Side lava pool
        { x: 3650, y: 1500, w: 300, h: 700 }
    ],

    windZones: [
        // Hot air vents pushing upward
        { x: 1500, y: 1200, w: 200, h: 400, forceX: 0, forceY: -2.0 },
        { x: 3100, y: 1000, w: 200, h: 400, forceX: 0, forceY: -1.5 }
    ],

    trampolines: [
        { x: 850, y: 1050, w: 70 },
        { x: 2800, y: 1400, w: 80 },
        { x: 3650, y: 1300, w: 70 }
    ],

    speedrunTargets: { gold: 70, silver: 95, bronze: 125 }
};

export default Level18_VolcanicDepths;
